<template>
  <div class="dashboard lvyun-dashboard">
    <div class="workbench-header">
      <h1>工作台</h1>
      <p class="workbench-greet">{{ greetText }}</p>
    </div>

    <div class="toolbar app-card-lg">
      <div class="toolbar-left">
        <span class="toolbar-label">统计周期</span>
        <div class="range-btn-group">
          <el-button
            size="small"
            round
            :type="rangeKey === 'today' ? 'primary' : 'default'"
            :plain="rangeKey !== 'today'"
            @click="selectRange('today')"
          >今日</el-button>
          <el-button
            size="small"
            round
            :type="rangeKey === 'week' ? 'primary' : 'default'"
            :plain="rangeKey !== 'week'"
            @click="selectRange('week')"
          >本周</el-button>
          <el-button
            size="small"
            round
            :type="rangeKey === 'month' ? 'primary' : 'default'"
            :plain="rangeKey !== 'month'"
            @click="selectRange('month')"
          >本月</el-button>
          <el-button
            size="small"
            round
            :type="rangeKey === 'custom' ? 'primary' : 'default'"
            :plain="rangeKey !== 'custom'"
            @click="selectRange('custom')"
          >自定义</el-button>
        </div>
        <el-date-picker
          v-if="rangeKey === 'custom'"
          v-model="customRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始"
          end-placeholder="结束"
          value-format="yyyy-MM-dd HH:mm:ss"
          format="yyyy-MM-dd HH:mm"
          style="width: 340px;"
          @change="onCustomChange"
        />
        <el-button
          v-if="!staffCashierNoStats"
          type="primary"
          size="small"
          class="btn-primary"
          round
          :loading="loading"
          @click="loadDashboard(true)"
        >刷新</el-button>
      </div>
      <div v-if="!staffCashierNoStats" class="toolbar-hint">数据每 30 秒自动同步；营业额 = 订单金额 − 行优惠汇总（已支付订单）</div>
    </div>

    <el-alert
      v-if="staffCashierNoStats"
      type="info"
      :closable="false"
      show-icon
      class="dashboard-restrict-alert"
      title="当前账号无法查看门店营业额与经营统计"
      description="可使用下方入口处理订单、兑换券与积分商城等业务；如需查看数据请使用超级管理员或管理员账号。"
    />

    <div v-if="staffCashierNoStats" class="staff-quick-links app-card-lg">
      <el-button type="primary" round icon="el-icon-document" @click="goToOrders">线上订单</el-button>
      <el-button type="primary" round plain icon="el-icon-document" @click="goToSituation">收银台订单</el-button>
      <el-button type="primary" round plain icon="el-icon-s-ticket" @click="goToVouchers">兑换券管理</el-button>
      <el-button type="primary" round plain icon="el-icon-present" @click="goToPointsMall">积分商城</el-button>
    </div>

    <el-skeleton v-if="!staffCashierNoStats" :loading="loading && !dashboard" animated :rows="6" class="stats-skeleton">
      <template slot="template">
        <el-skeleton-item variant="h1" style="width: 40%; margin: 12px 0;" />
        <el-skeleton-item variant="p" style="width: 90%; margin: 0 0 24px;" />
      </template>
      <div v-if="dashboard" class="kpi-row">
        <div class="kpi-card" @click="goToOrders">
          <div class="kpi-icon kpi-icon--blue"><i class="el-icon-wallet"></i></div>
          <div class="kpi-body">
            <div class="kpi-value">¥{{ fmtMoney(periodSafe.revenue) }}</div>
            <div class="kpi-label">支付金额</div>
          </div>
        </div>
        <div class="kpi-card" @click="goToOrders">
          <div class="kpi-icon kpi-icon--orange"><i class="el-icon-s-order"></i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ fmtInt(periodSafe.paidOrderCount) }}</div>
            <div class="kpi-label">支付订单数</div>
          </div>
        </div>
        <div class="kpi-card" @click="goToSubscriptionCalendar">
          <div class="kpi-icon kpi-icon--green"><i class="el-icon-date"></i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ fmtInt(subscriptionSafe.newCount) }}</div>
            <div class="kpi-label">随心订新单</div>
          </div>
        </div>
        <div class="kpi-card" @click="goToOrders">
          <div class="kpi-icon kpi-icon--purple"><i class="el-icon-box"></i></div>
          <div class="kpi-body">
            <div class="kpi-value">{{ fmtInt(summarySafe.pendingShipCount) }}</div>
            <div class="kpi-label">待发货</div>
          </div>
        </div>
        <div class="kpi-card" @click="goToOrders">
          <div class="kpi-icon kpi-icon--teal"><i class="el-icon-bank-card"></i></div>
          <div class="kpi-body">
            <div class="kpi-value">¥{{ fmtMoney(financialSafe.pendingSettlement) }}</div>
            <div class="kpi-label">待结算</div>
          </div>
        </div>
      </div>
    </el-skeleton>

    <div class="recent-row" v-if="!staffCashierNoStats && dashboard">
      <div class="recent-card app-card-lg">
        <div class="recent-card-head">
          <span class="recent-card-title">最近订单</span>
          <a href="#" class="recent-card-link" @click.prevent="goToOrders">查看全部</a>
        </div>
        <div class="recent-card-body">
          <i class="el-icon-document recent-empty-icon"></i>
          <p class="recent-empty-text">暂无列表预览，请在订单管理中查看与处理订单。</p>
          <el-button type="primary" icon="el-icon-plus" round @click="goToOrders">订单管理</el-button>
        </div>
      </div>
      <div class="recent-card app-card-lg">
        <div class="recent-card-head">
          <span class="recent-card-title">随心订</span>
          <a href="#" class="recent-card-link" @click.prevent="goToSubscriptionCalendar">查看全部</a>
        </div>
        <div class="recent-card-body recent-card-body--compact">
          <div class="recent-mini-grid">
            <div><span>新订单</span><strong>{{ fmtInt(subscriptionSafe.newCount) }}</strong></div>
            <div><span>套餐销售额</span><strong>¥{{ fmtMoney(subscriptionSafe.revenue) }}</strong></div>
            <div><span>周餐</span><strong>{{ fmtInt(packSafe.week.count) }} 单</strong></div>
            <div><span>月餐</span><strong>{{ fmtInt(packSafe.month.count) }} 单</strong></div>
          </div>
          <el-button type="primary" icon="el-icon-plus" round @click="goToSubscriptionCalendar">随心订日历</el-button>
        </div>
      </div>
      <div class="recent-card app-card-lg">
        <div class="recent-card-head">
          <span class="recent-card-title">商品与库存</span>
          <a href="#" class="recent-card-link" @click.prevent="goToProducts">查看全部</a>
        </div>
        <div class="recent-card-body">
          <i class="el-icon-goods recent-empty-icon"></i>
          <p class="recent-empty-text">
            商品总数 {{ fmtInt(summarySafe.productCount) }} · 门店在售 {{ fmtInt(summarySafe.storeOnSale) }}
          </p>
          <el-button type="primary" icon="el-icon-plus" round @click="goToProducts">商品管理</el-button>
        </div>
      </div>
    </div>

    <el-collapse v-if="!staffCashierNoStats && dashboard" v-model="detailCollapse" class="detail-collapse app-card-lg">
      <el-collapse-item title="详细经营数据（指标网格、实时概况、图表）" name="detail">
        <div class="function-nav app-card-lg function-nav--embedded">
          <div class="nav-item" @click="goToEmployee">
            <svg class="nav-icon icon-employee" viewBox="0 0 24 24" role="img" aria-label="员工管理" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <span>员工管理</span>
          </div>
          <div class="nav-item" @click="goToProductLibrary">
            <svg class="nav-icon icon-product" viewBox="0 0 24 24" role="img" aria-label="商品库" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3.5" y="6.5" width="17" height="15" rx="2"/>
              <path d="M7.5 6.5v15"/>
              <path d="M3.5 11.5h17"/>
              <path d="M3.5 16.5h17"/>
            </svg>
            <span>商品库</span>
          </div>
          <div class="nav-item" @click="goToOrderShipment">
            <svg class="nav-icon icon-shipment" viewBox="0 0 24 24" role="img" aria-label="订单发货" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 17h2"/>
              <path d="M6 17H16"/>
              <path d="M17 17h4"/>
              <path d="M3 7h11l2 3h5v7H3V7z"/>
              <path d="M14 10h6"/>
              <circle cx="7" cy="17" r="2"/>
              <circle cx="17" cy="17" r="2"/>
            </svg>
            <span>订单发货</span>
          </div>
          <div class="nav-item" @click="goToOrderQuery">
            <svg class="nav-icon icon-order-query" viewBox="0 0 24 24" role="img" aria-label="订单查询" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="7"/>
              <path d="M21 21l-3.8-3.8"/>
              <path d="M7.5 10.5h7"/>
              <path d="M7.5 14h10"/>
            </svg>
            <span>订单查询</span>
          </div>
          <div class="nav-item" @click="goToCustomerQuery">
            <svg class="nav-icon icon-customer-query" viewBox="0 0 24 24" role="img" aria-label="客户查询" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="8" r="4"/>
              <path d="M4 21a8 8 0 0 1 16 0"/>
            </svg>
            <span>客户查询</span>
          </div>
          <div class="nav-item" @click="goToVouchers">
            <svg class="nav-icon icon-voucher" viewBox="0 0 24 24" role="img" aria-label="兑换券管理" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M7 4h10a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V6a2 2 0 0 1 2-2z"/>
              <path d="M12 7v10"/>
            </svg>
            <span>兑换券管理</span>
          </div>
        </div>

        <div class="app-card-lg stats-wrapper stats-wrapper--embedded">
          <div class="section-header">
            <span>关键指标概览</span>
          </div>
          <div class="stats-container">
            <div class="stat-card" @click="goToUsers">
              <div class="stat-title">客户数量</div>
              <div class="stat-value">{{ fmtInt(summarySafe.customerCount) }}</div>
            </div>
            <div class="stat-card" @click="goToProducts">
              <div class="stat-title">商品总数</div>
              <div class="stat-value">{{ fmtInt(summarySafe.productCount) }}</div>
            </div>
            <div class="stat-card" @click="goToOrders">
              <div class="stat-title">订单数</div>
              <div class="stat-value">{{ fmtInt(summarySafe.orderCount) }}</div>
            </div>
            <div class="stat-card" @click="goToVouchers">
              <div class="stat-title">兑换券总数</div>
              <div class="stat-value">{{ fmtInt(summarySafe.voucherCount) }}</div>
            </div>
            <div class="stat-card" @click="goToOrders">
              <div class="stat-title">待发货订单</div>
              <div class="stat-value">{{ fmtInt(summarySafe.pendingShipCount) }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-title">待处理退款</div>
              <div class="stat-value">{{ fmtInt(summarySafe.pendingRefundCount) }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-title">未读客户消息</div>
              <div class="stat-value">{{ fmtInt(summarySafe.unreadMessages) }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-title">未读通知</div>
              <div class="stat-value">{{ fmtInt(summarySafe.unreadNotifications) }}</div>
            </div>
            <div class="stat-card" @click="goToProducts">
              <div class="stat-title">门店在售</div>
              <div class="stat-value">{{ fmtInt(summarySafe.storeOnSale) }}</div>
            </div>
            <div class="stat-card" @click="goToProducts">
              <div class="stat-title">网店在售</div>
              <div class="stat-value">{{ fmtInt(summarySafe.onlineOnSale) }}</div>
            </div>
            <div class="stat-card" @click="goToProducts">
              <div class="stat-title">门店售罄</div>
              <div class="stat-value">{{ fmtInt(summarySafe.storeSoldOut) }}</div>
            </div>
            <div class="stat-card" @click="goToProducts">
              <div class="stat-title">网店售罄</div>
              <div class="stat-value">{{ fmtInt(summarySafe.onlineSoldOut) }}</div>
            </div>
          </div>
        </div>

        <div class="dashboard-detail-layout">
          <div class="realtime-section app-card-lg">
          <div class="section-header">
            <span>实时情况 · {{ rangeLabel }}</span>
            <span class="update-time">更新于：{{ dashboard.updatedAt || '—' }}</span>
          </div>
          <div class="realtime-stats">
          <div class="realtime-item">
            <div class="item-label">支付订单数</div>
            <div class="item-value">{{ fmtInt(periodSafe.paidOrderCount) }}</div>
            <div class="item-change positive">{{ compareLabel }} {{ fmtInt(periodSafe.prevPaidOrderCount) }}</div>
            <div class="item-percent">{{ momPercent(periodSafe.percentPaidChange) }}</div>
          </div>
          <div class="realtime-item">
            <div class="item-label">支付金额(元)</div>
            <div class="item-value">¥{{ fmtMoney(periodSafe.revenue) }}</div>
            <div class="item-change positive">{{ compareLabel }} ¥{{ fmtMoney(periodSafe.prevRevenue) }}</div>
            <div class="item-percent">{{ momPercent(periodSafe.percentRevenueChange) }}</div>
          </div>
          <div class="realtime-item">
            <div class="item-label">本月目标营业额(元)</div>
            <div class="item-value">¥{{ fmtMoney(periodSafe.monthlyTarget) }}</div>
            <div class="item-change muted">环境变量 DASHBOARD_MONTHLY_TARGET</div>
            <div class="item-percent">—</div>
          </div>
          <div class="realtime-item">
            <div class="item-label">本月完成进度</div>
            <div class="item-value">{{ monthProgressText }}</div>
            <div class="item-change muted">基于本月已支付订单营业额</div>
            <div class="item-percent">{{ monthProgressPercent }}</div>
          </div>
          <div class="realtime-item">
            <div class="item-label">访客数</div>
            <div class="item-value">{{ fmtInt(periodSafe.visitorCount) }}</div>
            <div class="item-change muted">待接入埋点</div>
            <div class="item-percent">—</div>
          </div>
          <div class="realtime-item">
            <div class="item-label">累计客户数</div>
            <div class="item-value">{{ fmtInt(summarySafe.customerCount) }}</div>
            <div class="item-change muted">用户表总数</div>
            <div class="item-percent">—</div>
          </div>
          <div class="realtime-item">
            <div class="item-label">客单价(元)</div>
            <div class="item-value">¥{{ fmtMoney(avgPrice) }}</div>
            <div class="item-change muted">营业额 ÷ 支付订单数</div>
            <div class="item-percent">—</div>
          </div>
          <div class="realtime-item">
            <div class="item-label">支付转化率</div>
            <div class="item-value">{{ formatPercent1(periodSafe.conversionRate) }}%</div>
            <div class="item-change muted">支付成功 / 下单数(未取消)</div>
            <div class="item-percent">—</div>
          </div>
        </div>

        <div class="sub-block">
          <div class="sub-title">随心订套餐（{{ rangeLabel }} 新订）</div>
          <div class="sub-grid">
            <div class="sub-item">
              <span>新订单数</span>
              <strong>{{ fmtInt(subscriptionSafe.newCount) }}</strong>
            </div>
            <div class="sub-item">
              <span>套餐销售额</span>
              <strong>¥{{ fmtMoney(subscriptionSafe.revenue) }}</strong>
            </div>
            <div class="sub-item">
              <span>周餐</span>
              <strong>{{ fmtInt(packSafe.week.count) }} 单 / ¥{{ fmtMoney(packSafe.week.revenue) }}</strong>
            </div>
            <div class="sub-item">
              <span>月餐</span>
              <strong>{{ fmtInt(packSafe.month.count) }} 单 / ¥{{ fmtMoney(packSafe.month.revenue) }}</strong>
            </div>
            <div class="sub-item">
              <span>季餐</span>
              <strong>{{ fmtInt(packSafe.quarter.count) }} 单 / ¥{{ fmtMoney(packSafe.quarter.revenue) }}</strong>
            </div>
            <div class="sub-item">
              <span>年餐</span>
              <strong>{{ fmtInt(packSafe.year.count) }} 单 / ¥{{ fmtMoney(packSafe.year.revenue) }}</strong>
            </div>
          </div>
        </div>

        <div class="charts-row">
          <div class="chart-panel">
            <div class="chart-title">营业额趋势（按日）</div>
            <div v-if="!trendHasData" class="chart-empty">暂无数据</div>
            <div v-else class="trend-bars">
              <div v-for="(t, i) in trendSafe" :key="i" class="trend-cell">
                <div class="trend-bar-wrap">
                  <div class="trend-bar" :style="{ height: trendBarHeight(t.revenue) + '%' }" :title="'¥' + t.revenue"></div>
                </div>
                <div class="trend-label">{{ shortDate(t.date) }}</div>
              </div>
            </div>
          </div>
          <div class="chart-panel">
            <div class="chart-title">随心订套餐占比（单量）</div>
            <div v-if="!pieHasData" class="chart-empty">暂无数据</div>
            <div v-else class="pie-wrap">
              <div class="pie-visual" :style="pieConicStyle"></div>
              <ul class="pie-legend">
                <li v-for="p in pieLegend" :key="p.key">
                  <i class="dot" :style="{ background: p.color }"></i>
                  {{ p.name }} {{ formatPercent1(p.pct) }}%（{{ fmtInt(p.count) }}单）
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="financial-stats">
          <div class="financial-item">
            <div class="item-label">可用余额(元)</div>
            <div class="item-value">¥{{ fmtMoney(financialSafe.availableBalance) }}</div>
            <div class="item-change muted">可配置 DASHBOARD_AVAILABLE_BALANCE</div>
          </div>
          <div class="financial-item">
            <div class="item-label">待结算(元)</div>
            <div class="item-value">¥{{ fmtMoney(financialSafe.pendingSettlement) }}</div>
            <div class="item-change muted">已支付且未「已完成」订单营业额</div>
          </div>
        </div>
      </div>

      <div class="recommendation-section">
        <div class="recommendation-card app-card-lg">
          <div class="card-header">
            <span>重磅推出回头客好店</span>
            <span class="go-link">去看看</span>
          </div>
          <div class="card-content">
            <img src="https://via.placeholder.com/200x100" alt="回头客好店" class="card-image" />
            <p class="card-desc">消费真消费真体验真推荐</p>
          </div>
        </div>
      </div>
        </div>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
import { fetchDashboard } from '../api/dashboard'
import { getUserInfo } from '../utils/auth'
import { formatApiError } from '../utils/apiError'
import {
  formatCount,
  formatCurrencyYuan,
  formatPercent1,
  formatMoMPercent,
  averageOrderValue
} from '../utils/calculate'

const PIE_COLORS = ['#121212', '#2ECC71', '#F59E0B', '#165DFF', '#94A3B8']

export default {
  name: 'Dashboard',
  data() {
    return {
      loading: false,
      rangeKey: 'today',
      customRange: null,
      dashboard: null,
      pollTimer: null,
      /** 短缓存：减少闪烁 */
      lastParamsKey: '',
      /** 详细数据折叠面板（默认收起，首屏对标律智云工作台） */
      detailCollapse: [],
      /** 员工 / 收银员：接口禁止查看营业额统计 */
      staffCashierNoStats: false
    }
  },
  computed: {
    summarySafe() {
      return (this.dashboard && this.dashboard.summary) || {}
    },
    periodSafe() {
      return (this.dashboard && this.dashboard.period) || {}
    },
    subscriptionSafe() {
      return (this.dashboard && this.dashboard.subscription) || {}
    },
    packSafe() {
      const p = this.subscriptionSafe.packages || {}
      return {
        week: p.week || { count: 0, revenue: 0 },
        month: p.month || { count: 0, revenue: 0 },
        quarter: p.quarter || { count: 0, revenue: 0 },
        year: p.year || { count: 0, revenue: 0 }
      }
    },
    financialSafe() {
      return (this.dashboard && this.dashboard.financial) || {}
    },
    trendSafe() {
      const ch = this.dashboard && this.dashboard.charts
      return (ch && ch.trend) || []
    },
    compareLabel() {
      return (this.dashboard && this.dashboard.compare && this.dashboard.compare.label) || '对比'
    },
    rangeLabel() {
      return (this.dashboard && this.dashboard.range && this.dashboard.range.label) || ''
    },
    trendMax() {
      let m = 0
      this.trendSafe.forEach((t) => {
        if (t.revenue > m) m = t.revenue
      })
      return m > 0 ? m : 1
    },
    trendHasData() {
      return this.trendSafe.some((t) => t.revenue > 0 || t.placedOrders > 0)
    },
    pieHasData() {
      const pie = this.dashboard && this.dashboard.charts && this.dashboard.charts.packagePie
      return pie && pie.total > 0
    },
    pieLegend() {
      const pie = this.dashboard && this.dashboard.charts && this.dashboard.charts.packagePie
      if (!pie || !pie.parts) return []
      const total = pie.total || 1
      return pie.parts
        .filter((p) => p.key !== 'unknown' || p.count > 0)
        .map((p, i) => ({
          ...p,
          pct: (p.count / total) * 100,
          color: PIE_COLORS[i % PIE_COLORS.length]
        }))
    },
    pieConicStyle() {
      const pie = this.dashboard && this.dashboard.charts && this.dashboard.charts.packagePie
      if (!pie || !pie.parts || !pie.total) {
        return { background: '#e5e7eb' }
      }
      const total = pie.total
      let acc = 0
      const stops = []
      pie.parts.forEach((p, i) => {
        if (!p.count) return
        const deg = (p.count / total) * 360
        const start = acc
        acc += deg
        const c = PIE_COLORS[i % PIE_COLORS.length]
        stops.push(`${c} ${start}deg ${acc}deg`)
      })
      if (!stops.length) return { background: '#e5e7eb' }
      if (acc < 360) {
        stops.push(`#e5e7eb ${acc}deg 360deg`)
      }
      return { background: `conic-gradient(${stops.join(',')})` }
    },
    avgPrice() {
      return averageOrderValue(this.periodSafe.revenue, this.periodSafe.paidOrderCount)
    },
    monthProgressText() {
      const t = Number(this.periodSafe.monthlyTarget)
      if (!t || t <= 0) return '—'
      return `¥${formatCurrencyYuan(this.periodSafe.revenueThisMonth)}`
    },
    monthProgressPercent() {
      const t = Number(this.periodSafe.monthlyTarget)
      if (!t || t <= 0) return '—'
      const p = this.periodSafe.monthlyProgress
      return p != null ? `${formatPercent1(p)}%` : '—'
    },
    greetText() {
      const u = getUserInfo() || {}
      const name = String(u.displayName || u.username || '').trim()
      return name ? `${name}，欢迎回来。` : '你好，欢迎回来。'
    }
  },
  mounted() {
    this.loadDashboard(true)
    this.startPolling()
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  },
  methods: {
    fmtInt: formatCount,
    fmtMoney: formatCurrencyYuan,
    formatPercent1,
    momPercent: formatMoMPercent,
    shortDate(d) {
      if (!d || typeof d !== 'string') return ''
      return d.slice(5)
    },
    trendBarHeight(rev) {
      const r = Number(rev) || 0
      return Math.max(4, Math.round((r / this.trendMax) * 100))
    },
    paramsKey() {
      if (this.rangeKey === 'custom' && this.customRange && this.customRange.length === 2) {
        return `custom|${this.customRange[0]}|${this.customRange[1]}`
      }
      return this.rangeKey
    },
    buildParams() {
      const base = { range: this.rangeKey }
      if (this.rangeKey === 'custom' && this.customRange && this.customRange.length === 2) {
        base.start = this.customRange[0]
        base.end = this.customRange[1]
      }
      return base
    },
    async loadDashboard(showLoading = false) {
      if (this.staffCashierNoStats) return
      if (this.rangeKey === 'custom' && (!this.customRange || this.customRange.length !== 2)) {
        this.$message.warning('请选择自定义时间范围')
        return
      }
      const key = this.paramsKey()
      if (showLoading) this.loading = true
      try {
        const resp = await fetchDashboard(this.$api, this.buildParams())
        if (resp && resp.code === 200 && resp.data) {
          this.staffCashierNoStats = false
          this.dashboard = resp.data
          this.lastParamsKey = key
        } else {
          this.$message.error((resp && resp.message) || '仪表盘数据获取失败')
        }
      } catch (e) {
        if (e && e.response && e.response.status === 403) {
          this.staffCashierNoStats = true
          this.dashboard = null
          if (this.pollTimer) {
            clearInterval(this.pollTimer)
            this.pollTimer = null
          }
          if (showLoading) {
            const m =
              (e.response.data && e.response.data.message) ||
              '当前账号无法查看门店营业额与统计数据'
            this.$message.info(m)
          }
          return
        }
        const msg = formatApiError(e, '仪表盘接口不可用，请确认后端已启动')
        this.$message.error(msg)
        this.dashboard = null
      } finally {
        this.loading = false
      }
    },
    startPolling() {
      if (this.staffCashierNoStats) return
      this.pollTimer = setInterval(() => {
        if (document.hidden) return
        if (this.staffCashierNoStats) return
        this.loadDashboard(false)
      }, 30000)
    },
    selectRange(key) {
      if (this.rangeKey === key) return
      this.rangeKey = key
      this.onRangeChange()
    },
    onRangeChange() {
      if (this.rangeKey !== 'custom') {
        this.loadDashboard(true)
      }
    },
    onCustomChange() {
      if (this.rangeKey === 'custom' && this.customRange && this.customRange.length === 2) {
        this.loadDashboard(true)
      }
    },
    goToEmployee() {
      this.$router.push('/employees')
    },
    goToProductLibrary() {
      this.$router.push('/products')
    },
    goToOrderShipment() {
      this.$router.push('/orders')
    },
    goToOrderQuery() {
      this.$router.push('/orders/situation')
    },
    goToCustomerQuery() {
      this.$router.push('/users')
    },
    goToVouchers() {
      this.$router.push('/vouchers')
    },
    goToSituation() {
      this.$router.push('/orders/situation')
    },
    goToPointsMall() {
      this.$router.push('/member/points-mall')
    },
    goToUsers() {
      this.$router.push('/users')
    },
    goToProducts() {
      this.$router.push('/products')
    },
    goToOrders() {
      this.$router.push('/orders')
    },
    goToSubscriptionCalendar() {
      this.$router.push({ path: '/meal-holiday-config', query: { tab: 'calendar' } })
    }
  }
}
</script>

<style scoped>
.lvyun-dashboard {
  padding: 0;
  background: transparent;
  min-height: calc(100vh - 60px);
}

.workbench-header {
  margin-bottom: 22px;
}

.workbench-header h1 {
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.01em;
  font-family: var(--app-font);
}

.workbench-greet {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding: 16px 22px;
  margin: 0 0 20px;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.toolbar-label {
  font-size: 14px;
  color: #4b5563;
  margin-right: 4px;
}

.range-btn-group {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.toolbar-hint {
  font-size: 12px;
  color: #9ca3af;
  max-width: 420px;
  line-height: 1.4;
}

.stats-skeleton {
  margin: 0 0 20px;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

@media (max-width: 1200px) {
  .kpi-row {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .kpi-row {
    grid-template-columns: 1fr 1fr;
  }
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 18px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 28px rgba(15, 23, 42, 0.06);
  transition: border-color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease;
}

.kpi-card:hover {
  border-color: rgba(43, 182, 115, 0.28);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06), 0 24px 48px rgba(15, 23, 42, 0.1);
}

.kpi-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);
}

.kpi-icon--blue {
  background: #e8f4fd;
  color: #1976d2;
}

.kpi-icon--orange {
  background: #fff4e6;
  color: #e65100;
}

.kpi-icon--green {
  background: #e8faf0;
  color: #1e8f4a;
}

.kpi-icon--purple {
  background: #f3e8ff;
  color: #7b1fa2;
}

.kpi-icon--teal {
  background: #e0f7fa;
  color: #00838f;
}

.kpi-body {
  min-width: 0;
}

.kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #111111;
  line-height: 1.2;
  word-break: break-all;
}

.kpi-label {
  margin-top: 4px;
  font-size: 13px;
  color: #888888;
}

.recent-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  margin-bottom: 20px;
}

@media (max-width: 1100px) {
  .recent-row {
    grid-template-columns: 1fr;
  }
}

.recent-card {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 280px;
}

.recent-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.recent-card-title {
  font-size: 16px;
  font-weight: 600;
  color: #111111;
}

.recent-card-link {
  font-size: 13px;
  color: #666666;
  cursor: pointer;
  text-decoration: none;
}

.recent-card-link:hover {
  color: #121212;
  text-decoration: underline;
}

.recent-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 20px 20px;
  text-align: center;
}

.recent-card-body--compact {
  align-items: stretch;
  justify-content: space-between;
}

.recent-empty-icon {
  font-size: 52px;
  color: #dddddd;
  margin-bottom: 12px;
}

.recent-empty-text {
  margin: 0 0 20px;
  font-size: 14px;
  color: #999999;
  line-height: 1.5;
}

.recent-mini-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  text-align: left;
  margin-bottom: 16px;
  width: 100%;
}

.recent-mini-grid span {
  display: block;
  font-size: 12px;
  color: #888888;
  margin-bottom: 4px;
}

.recent-mini-grid strong {
  font-size: 15px;
  color: #111111;
}

.detail-collapse {
  border: 1px solid #eeeeee !important;
  padding: 0 16px 12px;
}

.detail-collapse ::v-deep(.el-collapse-item__header) {
  font-size: 15px;
  font-weight: 600;
  color: #333333;
  height: 52px;
  line-height: 52px;
  padding-left: 4px;
}

.detail-collapse ::v-deep(.el-collapse-item__wrap) {
  border-bottom: none;
}

.detail-collapse ::v-deep(.el-collapse-item__content) {
  padding-bottom: 8px;
}

.function-nav {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 16px 20px;
  margin: 0 0 16px;
}

.function-nav--embedded {
  margin-top: 0;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 10px 18px;
  border-radius: var(--radius-md);
  background: #f9fafb;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
}

.nav-item:hover {
  transform: translateY(-1px);
  background: #f0f0f0;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
}

.nav-icon {
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
  display: block;
  color: var(--primary-color);
}

.icon-employee { color: #165DFF; }
.icon-product { color: #F59E0B; }
.icon-shipment { color: #F59E0B; }
.icon-order-query { color: #165DFF; }
.icon-customer-query { color: #409EFF; }
.icon-voucher { color: #A855F7; }

.nav-item span {
  font-size: 14px;
  color: #111827;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 15px;
}

.stats-wrapper {
  margin: 0 0 20px;
}

.stats-wrapper--embedded {
  margin-bottom: 16px;
}

.stat-card {
  background-color: #fafafa;
  border-radius: 14px;
  border: 1px solid #eeeeee;
  padding: 18px 16px;
  box-shadow: none;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: none;
  border-color: #dddddd;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.05);
}

.stat-title {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 26px;
  font-weight: 600;
  color: #111827;
}

.dashboard-detail-layout {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  padding: 0 0 8px;
}

.realtime-section {
  flex: 1;
  min-width: 280px;
  background-color: #ffffff;
  border-radius: 16px;
  padding: 20px;
  border: 1px solid #eeeeee;
  box-shadow: none;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eaeaea;
}

.section-header span:first-child {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.update-time {
  font-size: 12px;
  color: #6b7280;
}

.realtime-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.realtime-item {
  padding: 15px;
  border-radius: 10px;
  background-color: #f3f4f6;
}

.item-label {
  font-size: 14px;
  color: #4b5563;
  margin-bottom: 8px;
}

.item-value {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 5px;
}

.item-change {
  font-size: 12px;
  margin-bottom: 5px;
}

.item-change.positive { color: #4caf50; }
.item-change.negative { color: #f44336; }
.item-change.muted { color: #9ca3af; }

.item-percent {
  font-size: 12px;
  color: #999;
}

.sub-block {
  margin-bottom: 20px;
  padding: 16px;
  border-radius: 10px;
  background: #f9fafb;
}

.sub-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.sub-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.sub-item {
  font-size: 13px;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sub-item strong {
  color: #111827;
  font-size: 15px;
}

.charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.chart-panel {
  padding: 16px;
  border-radius: 10px;
  background: #f3f4f6;
  min-height: 220px;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.chart-empty {
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  padding: 48px 0;
}

.trend-bars {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 4px;
  height: 160px;
  padding-top: 8px;
}

.trend-cell {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
}

.trend-bar-wrap {
  width: 100%;
  height: 120px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.trend-bar {
  width: 70%;
  max-width: 24px;
  min-height: 4px;
  background: linear-gradient(180deg, #34d399 0%, #10b981 100%);
  border-radius: 4px 4px 0 0;
  transition: height 0.3s ease;
}

.trend-label {
  font-size: 10px;
  color: #6b7280;
  margin-top: 6px;
  transform: scale(0.9);
}

.pie-wrap {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.pie-visual {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  flex-shrink: 0;
}

.pie-legend {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 13px;
  color: #4b5563;
}

.pie-legend li {
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.pie-legend .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.financial-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.financial-item {
  padding: 15px;
  border-radius: 10px;
  background-color: #f3f4f6;
}

.recommendation-section {
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.recommendation-card {
  background-color: #ffffff;
  border-radius: 16px;
  padding: 20px;
  border: 1px solid #eeeeee;
  box-shadow: none;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eaeaea;
}

.card-header span:first-child {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.go-link {
  font-size: 12px;
  color: #409EFF;
  cursor: pointer;
}

.card-image {
  width: 100%;
  border-radius: 8px;
  margin-bottom: 10px;
}

.card-desc {
  font-size: 14px;
  color: #4b5563;
  text-align: center;
}

.dashboard-restrict-alert {
  margin-bottom: 16px;
}

.staff-quick-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px;
  margin-bottom: 20px;
}
</style>
